/**
 * Audio Feature Extractor
 *
 * Computes enhanced audio features from frequency data with lazy/on-demand evaluation.
 * Features are only computed when accessed, ensuring zero overhead for visualizers
 * that don't use them.
 *
 * Features available:
 * - bands: 7-band musical frequency separation (subBass through brilliance)
 * - spectral: centroid (brightness), rolloff, flux, flatness
 * - rhythm: beat/onset detection with adaptive thresholding
 * - pitch: CQT and FFT semitone remapping for musical visualization
 */

// Musical frequency band definitions (Hz)
const FREQUENCY_BANDS = {
  subBass:    { low: 20,    high: 60    },  // Felt more than heard, rumble
  bass:       { low: 60,    high: 250   },  // Kick drums, bass guitar
  lowMid:     { low: 250,   high: 500   },  // Warmth, body of instruments
  mid:        { low: 500,   high: 2000  },  // Vocals, most instruments
  highMid:    { low: 2000,  high: 4000  },  // Presence, attack
  presence:   { low: 4000,  high: 6000  },  // Definition, clarity
  brilliance: { low: 6000,  high: 20000 }   // Air, sparkle, cymbals
};

// Musical note frequencies for semitone mapping (A0 = 27.5Hz)
const A0_FREQ = 27.5;

/**
 * Onset/Beat Detector using spectral flux with adaptive thresholding
 */
class OnsetDetector {
  constructor(options = {}) {
    this.historySize = options.historySize || 43;         // ~0.7s at 60fps
    this.threshold = options.threshold || 1.5;            // Multiplier above mean
    this.minTimeBetweenOnsets = options.minTimeBetweenOnsets || 6;  // frames (~100ms)

    this.fluxHistory = [];
    this.framesSinceLastOnset = this.minTimeBetweenOnsets; // Allow immediate first onset
  }

  /**
   * Detect onset from spectral flux value
   * @param {number} spectralFlux - Current frame's spectral flux (0-1)
   * @returns {{isOnset: boolean, onsetStrength: number, confidence: number}}
   */
  detect(spectralFlux) {
    this.fluxHistory.push(spectralFlux);
    if (this.fluxHistory.length > this.historySize) {
      this.fluxHistory.shift();
    }

    this.framesSinceLastOnset++;

    // Need enough history for adaptive threshold
    if (this.fluxHistory.length < 10) {
      return { isOnset: false, onsetStrength: spectralFlux, confidence: 0 };
    }

    // Compute adaptive threshold from history mean
    const mean = this.fluxHistory.reduce((a, b) => a + b, 0) / this.fluxHistory.length;
    const adaptiveThreshold = mean * this.threshold;

    // Detect onset: flux above threshold AND enough time since last onset
    const isOnset = spectralFlux > adaptiveThreshold &&
                    this.framesSinceLastOnset >= this.minTimeBetweenOnsets;

    if (isOnset) {
      this.framesSinceLastOnset = 0;
    }

    // Confidence: how far above threshold (clamped to 0-1)
    const confidence = isOnset && adaptiveThreshold > 0
      ? Math.min(1, (spectralFlux - adaptiveThreshold) / adaptiveThreshold)
      : 0;

    return {
      isOnset,
      onsetStrength: spectralFlux,
      confidence
    };
  }

  reset() {
    this.fluxHistory = [];
    this.framesSinceLastOnset = this.minTimeBetweenOnsets;
  }
}

/**
 * Main Audio Feature Extractor
 */
export class AudioFeatureExtractor {
  constructor(options = {}) {
    this.sampleRate = options.sampleRate || 44100;
    this.fftSize = options.fftSize || 2048;

    // State for temporal features
    this.previousSpectrum = null;
    this.onsetDetector = new OnsetDetector(options.onset);

    // Pre-compute bin ranges for frequency bands
    this._bandBins = this._computeBandBins();

    // Pre-compute semitone bin mappings (cached per binsPerOctave)
    this._semitoneCache = new Map();
  }

  /**
   * Pre-compute FFT bin indices for each frequency band
   */
  _computeBandBins() {
    const binWidth = this.sampleRate / this.fftSize;
    const maxBin = this.fftSize / 2 - 1;
    const bins = {};

    for (const [name, band] of Object.entries(FREQUENCY_BANDS)) {
      bins[name] = {
        low: Math.max(0, Math.floor(band.low / binWidth)),
        high: Math.min(maxBin, Math.ceil(band.high / binWidth))
      };
    }

    return bins;
  }

  /**
   * Extract all features from frequency data (lazy evaluation)
   * @param {Uint8Array} frequencyData - Raw FFT frequency data (0-255 values)
   * @param {Float32Array} [rawSamples] - Raw audio samples (required for CQT)
   * @returns {AudioFeatures} Feature object with lazy getters
   */
  extract(frequencyData, rawSamples = null) {
    const cache = {};
    const self = this;

    // Lazily capture previous spectrum only when spectral/rhythm features are accessed
    // This avoids memory allocation overhead when visualizers don't use these features
    let prevSpectrumCaptured = false;
    let prevSpectrum = null;

    const ensurePrevSpectrum = () => {
      if (!prevSpectrumCaptured) {
        prevSpectrum = this.previousSpectrum;
        this.previousSpectrum = new Uint8Array(frequencyData);
        prevSpectrumCaptured = true;
      }
      return prevSpectrum;
    };

    return {
      /**
       * 7-band musical frequency energy (0-1 each)
       */
      get bands() {
        return cache.bands ??= self._computeBands(frequencyData);
      },

      /**
       * Spectral features: centroid, rolloff, flux, flatness
       */
      get spectral() {
        return cache.spectral ??= self._computeSpectral(frequencyData, ensurePrevSpectrum());
      },

      /**
       * Beat/onset detection results
       */
      get rhythm() {
        // Rhythm depends on spectral.flux, so compute spectral first if needed
        if (!cache.spectral) {
          cache.spectral = self._computeSpectral(frequencyData, ensurePrevSpectrum());
        }
        return cache.rhythm ??= self.onsetDetector.detect(cache.spectral.flux);
      },

      /**
       * Pitch/musical analysis methods
       */
      pitch: {
        /**
         * Remap FFT bins to semitone buckets (fast approximation)
         * @param {number} [binsPerOctave=12] - Bins per octave (12=chromatic, 24=quarter-tone)
         * @returns {Float32Array} Semitone energies (normalized 0-1)
         */
        semitones(binsPerOctave = 12) {
          const key = `semi_${binsPerOctave}`;
          return cache[key] ??= self._computeSemitones(frequencyData, binsPerOctave);
        },

        /**
         * Compute true CQT from raw audio samples
         * @param {number} [binsPerOctave=12] - Bins per octave
         * @returns {Float32Array} CQT magnitudes (normalized 0-1)
         */
        cqt(binsPerOctave = 12) {
          if (!rawSamples) {
            console.warn('CQT requires raw audio samples. Falling back to semitones.');
            return this.semitones(binsPerOctave);
          }
          const key = `cqt_${binsPerOctave}`;
          return cache[key] ??= self._computeCQT(rawSamples, binsPerOctave);
        }
      },

      /** Original frequency data */
      raw: frequencyData
    };
  }

  /**
   * Compute 7-band frequency energies
   */
  _computeBands(frequencyData) {
    const bands = {};

    for (const [name, binRange] of Object.entries(this._bandBins)) {
      let sum = 0;
      let count = 0;

      for (let i = binRange.low; i <= binRange.high && i < frequencyData.length; i++) {
        sum += frequencyData[i];
        count++;
      }

      // Normalize to 0-1
      bands[name] = count > 0 ? (sum / count) / 255 : 0;
    }

    return bands;
  }

  /**
   * Compute spectral features
   */
  _computeSpectral(frequencyData, previousSpectrum) {
    return {
      centroid: this._computeCentroid(frequencyData),
      rolloff: this._computeRolloff(frequencyData),
      flux: this._computeFlux(frequencyData, previousSpectrum),
      flatness: this._computeFlatness(frequencyData)
    };
  }

  /**
   * Spectral Centroid - "brightness" (center of mass of spectrum)
   * @returns {number} Normalized 0-1 (higher = brighter)
   */
  _computeCentroid(frequencyData) {
    const binWidth = this.sampleRate / this.fftSize;
    let weightedSum = 0;
    let magnitudeSum = 0;

    for (let i = 0; i < frequencyData.length; i++) {
      const magnitude = frequencyData[i];
      const frequency = i * binWidth;
      weightedSum += magnitude * frequency;
      magnitudeSum += magnitude;
    }

    if (magnitudeSum === 0) return 0;

    const centroid = weightedSum / magnitudeSum;
    // Normalize: typical music centroid ranges ~500-5000Hz, cap at 8000Hz
    return Math.min(1, centroid / 8000);
  }

  /**
   * Spectral Rolloff - frequency below which 85% of energy lies
   * @returns {number} Normalized 0-1
   */
  _computeRolloff(frequencyData, rolloffPercent = 0.85) {
    let totalEnergy = 0;
    for (let i = 0; i < frequencyData.length; i++) {
      totalEnergy += frequencyData[i];
    }

    if (totalEnergy === 0) return 0;

    const threshold = totalEnergy * rolloffPercent;
    let cumulativeEnergy = 0;

    for (let i = 0; i < frequencyData.length; i++) {
      cumulativeEnergy += frequencyData[i];
      if (cumulativeEnergy >= threshold) {
        // Normalize to Nyquist frequency
        return i / frequencyData.length;
      }
    }

    return 1;
  }

  /**
   * Spectral Flux - rate of spectral change (high during transients)
   * Uses half-wave rectification (only counts increases)
   * @returns {number} Normalized 0-1
   */
  _computeFlux(currentSpectrum, previousSpectrum) {
    if (!previousSpectrum) return 0;

    let flux = 0;
    for (let i = 0; i < currentSpectrum.length; i++) {
      const diff = currentSpectrum[i] - previousSpectrum[i];
      // Half-wave rectification: only count positive changes
      if (diff > 0) {
        flux += diff * diff;
      }
    }

    // Normalize: max flux would be 255^2 * numBins
    return Math.sqrt(flux) / (255 * Math.sqrt(currentSpectrum.length));
  }

  /**
   * Spectral Flatness - tonal vs noisy content
   * Ratio of geometric mean to arithmetic mean
   * @returns {number} 0-1 (0=tonal, 1=noisy/white noise)
   */
  _computeFlatness(frequencyData) {
    let logSum = 0;
    let sum = 0;
    let count = 0;

    for (let i = 0; i < frequencyData.length; i++) {
      const value = frequencyData[i] + 1; // Add 1 to avoid log(0)
      logSum += Math.log(value);
      sum += value;
      count++;
    }

    if (count === 0 || sum === 0) return 0;

    const geometricMean = Math.exp(logSum / count);
    const arithmeticMean = sum / count;

    return geometricMean / arithmeticMean;
  }

  /**
   * Remap FFT bins to semitone buckets using logarithmic interpolation
   * @param {Uint8Array} frequencyData - FFT frequency data
   * @param {number} binsPerOctave - Bins per octave (12=chromatic)
   * @returns {Float32Array} Semitone energies
   */
  _computeSemitones(frequencyData, binsPerOctave) {
    // Get or create mapping for this binsPerOctave
    let mapping = this._semitoneCache.get(binsPerOctave);
    if (!mapping) {
      mapping = this._createSemitoneMapping(binsPerOctave);
      this._semitoneCache.set(binsPerOctave, mapping);
    }

    const result = new Float32Array(mapping.numBins);
    const binWidth = this.sampleRate / this.fftSize;

    for (let i = 0; i < mapping.numBins; i++) {
      const centerFreq = mapping.frequencies[i];
      const lowFreq = centerFreq / mapping.freqRatio;
      const highFreq = centerFreq * mapping.freqRatio;

      // Map frequency range to FFT bins
      const lowBin = Math.max(0, Math.floor(lowFreq / binWidth));
      const highBin = Math.min(frequencyData.length - 1, Math.ceil(highFreq / binWidth));

      let sum = 0;
      let count = 0;
      for (let j = lowBin; j <= highBin; j++) {
        sum += frequencyData[j];
        count++;
      }

      result[i] = count > 0 ? (sum / count) / 255 : 0;
    }

    return result;
  }

  /**
   * Create semitone frequency mapping
   */
  _createSemitoneMapping(binsPerOctave) {
    // Cover ~8 octaves (A0=27.5Hz to A8=7040Hz)
    const numOctaves = 8;
    const numBins = numOctaves * binsPerOctave;
    const frequencies = new Float32Array(numBins);

    // Frequency ratio between adjacent bins
    const freqRatio = Math.pow(2, 1 / (2 * binsPerOctave)); // Half-step for bin width

    for (let i = 0; i < numBins; i++) {
      // Each bin is a fraction of a semitone from A0
      frequencies[i] = A0_FREQ * Math.pow(2, i / binsPerOctave);
    }

    return { frequencies, numBins, freqRatio };
  }

  /**
   * Compute Constant-Q Transform from raw audio samples
   * This is computationally expensive but provides true logarithmic frequency resolution
   * @param {Float32Array} samples - Raw audio samples
   * @param {number} binsPerOctave - Bins per octave
   * @returns {Float32Array} CQT magnitudes (normalized 0-1)
   */
  _computeCQT(samples, binsPerOctave) {
    // CQT parameters
    const numOctaves = 8;
    const numBins = numOctaves * binsPerOctave;
    const result = new Float32Array(numBins);

    // Use a moderate Q factor - lower Q = wider bandwidth but less spectral leakage
    // Standard CQT uses Q = 1/(2^(1/B) - 1) ≈ 16.8 for B=12
    // We use a lower Q for better time resolution with short buffers
    const Q = 8;  // Reduced Q for better performance with short windows

    // Minimum window length to avoid noise from very short windows
    const minWindowLength = 128;

    for (let k = 0; k < numBins; k++) {
      const centerFreq = A0_FREQ * Math.pow(2, k / binsPerOctave);

      // Skip if frequency is above Nyquist
      if (centerFreq > this.sampleRate / 2) {
        result[k] = 0;
        continue;
      }

      // Window length for this frequency
      // For CQT: N_k = Q * fs / f_k (longer windows for lower frequencies)
      const idealWindowLength = Math.ceil(Q * this.sampleRate / centerFreq);
      const windowLength = Math.min(samples.length, Math.max(minWindowLength, idealWindowLength));

      // Skip if we don't have enough samples for reliable detection
      // Need at least 2 cycles of the frequency
      const cyclesAvailable = (windowLength * centerFreq) / this.sampleRate;
      if (cyclesAvailable < 1.5) {
        result[k] = 0;
        continue;
      }

      // Goertzel algorithm - efficient single-frequency DFT
      const omega = 2 * Math.PI * centerFreq / this.sampleRate;
      const coeff = 2 * Math.cos(omega);

      let s0 = 0, s1 = 0, s2 = 0;

      // Apply Hanning window and Goertzel iteration
      for (let n = 0; n < windowLength; n++) {
        const window = 0.5 * (1 - Math.cos(2 * Math.PI * n / windowLength));
        const sample = samples[n] * window;

        s0 = sample + coeff * s1 - s2;
        s2 = s1;
        s1 = s0;
      }

      // Compute magnitude from Goertzel state
      const real = s1 - s2 * Math.cos(omega);
      const imag = s2 * Math.sin(omega);
      const magnitude = Math.sqrt(real * real + imag * imag);

      // Normalize by window length - use consistent scaling
      // Scale factor derived empirically for typical audio levels
      const normalizedMag = magnitude / windowLength;

      // Apply fixed gain (user can adjust with gain slider)
      // Values are typically in range 0-0.1 for music, scale to 0-1
      result[k] = Math.min(1, normalizedMag * 10);
    }

    return result;
  }

  /**
   * Reset temporal state (call between tracks)
   */
  reset() {
    this.previousSpectrum = null;
    this.onsetDetector.reset();
  }
}

/**
 * Factory function for creating feature extractors
 * @param {object} options - Configuration options
 * @returns {AudioFeatureExtractor}
 */
export function createFeatureExtractor(options = {}) {
  return new AudioFeatureExtractor(options);
}

/**
 * @typedef {Object} AudioFeatures
 * @property {Object} bands - 7-band frequency energies (0-1 each)
 * @property {Object} spectral - Spectral features (centroid, rolloff, flux, flatness)
 * @property {Object} rhythm - Beat/onset detection (isOnset, onsetStrength, confidence)
 * @property {Object} pitch - Pitch analysis methods (semitones, cqt)
 * @property {Uint8Array} raw - Original frequency data
 */
